import React, { useEffect, useState } from "react";
import { api, getErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import {
  Link2, Plus, Trash2, Copy, Check, ExternalLink,
  MessageCircle, Globe, Loader2, MousePointerClick,
  DollarSign, ShoppingBag, KeyRound, ChevronDown, ChevronUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const PLATFORMS = [
  { value: "meta",    label: "Meta Ads (Facebook/Instagram)", icon: "/facebook.png" },
  { value: "google",  label: "Google Ads",   icon: "/icons/google-ads.webp" },
  { value: "tiktok",  label: "TikTok Ads",   icon: "/icons/tiktok-ads.png" },
  { value: "hotmart", label: "Hotmart",      icon: "/icons/hotmart.svg" },
  { value: "outro",   label: "Outro / Customizado", icon: null },
];

function PlatformIcon({ platform, size = 16 }) {
  const info = PLATFORMS.find(p => p.value === platform) || PLATFORMS[4];
  if (!info.icon) return <Link2 size={size} color="hsl(var(--muted-foreground))" style={{ flexShrink: 0 }} />;
  return (
    <img
      src={info.icon}
      alt={info.label}
      style={{ width: size, height: size, objectFit: "contain", flexShrink: 0, borderRadius: platform === "meta" ? "50%" : 0 }}
    />
  );
}

const fmtBRL = (n) => `R$ ${Number(n || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`;

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        toast.success("Link copiado!");
        setTimeout(() => setCopied(false), 1800);
      }}
      style={{
        display: "flex", alignItems: "center", gap: 6,
        padding: "6px 11px", borderRadius: 8,
        border: "1px solid hsl(var(--border))", background: "hsl(var(--secondary))",
        color: copied ? "#22c55e" : "hsl(var(--foreground))",
        fontSize: 12.5, fontWeight: 600, cursor: "pointer",
      }}
    >
      {copied ? <Check size={13} /> : <Copy size={13} />}
      {copied ? "Copiado" : "Copiar"}
    </button>
  );
}

function CreateLinkForm({ onCreated }) {
  const [platform, setPlatform] = useState("meta");
  const [utmSource, setUtmSource] = useState("");
  const [utmMedium, setUtmMedium] = useState("");
  const [utmCampaign, setUtmCampaign] = useState("");
  const [destinationType, setDestinationType] = useState("whatsapp");
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [whatsappMessage, setWhatsappMessage] = useState("");
  const [destinationUrl, setDestinationUrl] = useState("");
  const [label, setLabel] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!utmCampaign.trim()) { toast.error("Dê um nome pra essa campanha (utm_campaign)."); return; }
    if (destinationType === "whatsapp" && !whatsappNumber.trim()) { toast.error("Informe o número de WhatsApp de destino."); return; }
    if (destinationType === "landing" && !destinationUrl.trim()) { toast.error("Informe a URL da landing page de destino."); return; }

    setSaving(true);
    try {
      await api.post("/utm/links", {
        platform,
        utm_source: utmSource.trim(),
        utm_medium: utmMedium.trim(),
        utm_campaign: utmCampaign.trim(),
        destination_type: destinationType,
        whatsapp_number: whatsappNumber.trim(),
        whatsapp_message: whatsappMessage.trim(),
        destination_url: destinationUrl.trim(),
        label: label.trim(),
      });
      toast.success("Link criado! 🎉");
      setUtmSource(""); setUtmMedium(""); setUtmCampaign("");
      setWhatsappNumber(""); setWhatsappMessage(""); setDestinationUrl(""); setLabel("");
      onCreated();
    } catch (e) {
      toast.error(getErrorMessage(e, "Erro ao criar link"));
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: "100%", padding: "10px 12px", borderRadius: 10,
    border: "1px solid hsl(var(--border))", background: "hsl(var(--secondary))",
    color: "hsl(var(--foreground))", fontSize: 14, outline: "none",
  };
  const labelStyle = { fontSize: 12.5, fontWeight: 600, color: "hsl(var(--muted-foreground))", marginBottom: 6, display: "block" };

  return (
    <div style={{ background: "hsl(var(--secondary))", border: "1px solid hsl(var(--border))", borderRadius: 16, padding: 20, marginBottom: 24 }}>
      <h3 style={{ fontSize: 16, fontWeight: 700, color: "hsl(var(--foreground))", marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
        <Plus size={18} color="#3b82f6" /> Criar novo link rastreável
      </h3>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
        <div>
          <label style={labelStyle}>Plataforma de origem</label>
          <select value={platform} onChange={e => setPlatform(e.target.value)} style={inputStyle}>
            {PLATFORMS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Nome do link <span style={{ color: "hsl(var(--muted-foreground))", fontWeight: 400 }}>(opcional, pra você identificar)</span></label>
          <input value={label} onChange={e => setLabel(e.target.value)} placeholder="Ex: Promoção de julho" style={inputStyle} />
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 14 }}>
        <div>
          <label style={labelStyle}>utm_campaign <span style={{ color: "#ef4444" }}>*</span></label>
          <input value={utmCampaign} onChange={e => setUtmCampaign(e.target.value)} placeholder="black_friday_2026" style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>utm_source</label>
          <input value={utmSource} onChange={e => setUtmSource(e.target.value)} placeholder="facebook" style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>utm_medium</label>
          <input value={utmMedium} onChange={e => setUtmMedium(e.target.value)} placeholder="cpc" style={inputStyle} />
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>Pra onde esse link deve levar o cliente?</label>
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <button
            onClick={() => setDestinationType("whatsapp")}
            style={{
              flex: 1, padding: "9px 0", borderRadius: 10, cursor: "pointer", fontWeight: 600, fontSize: 13.5,
              border: `1px solid ${destinationType === "whatsapp" ? "#25d366" : "hsl(var(--border))"}`,
              background: destinationType === "whatsapp" ? "rgba(37,211,102,0.1)" : "hsl(var(--secondary))",
              color: destinationType === "whatsapp" ? "#25d366" : "hsl(var(--muted-foreground))",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}
          >
            <MessageCircle size={15} /> WhatsApp
          </button>
          <button
            onClick={() => setDestinationType("landing")}
            style={{
              flex: 1, padding: "9px 0", borderRadius: 10, cursor: "pointer", fontWeight: 600, fontSize: 13.5,
              border: `1px solid ${destinationType === "landing" ? "#3b82f6" : "hsl(var(--border))"}`,
              background: destinationType === "landing" ? "rgba(59,130,246,0.1)" : "hsl(var(--secondary))",
              color: destinationType === "landing" ? "#3b82f6" : "hsl(var(--muted-foreground))",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}
          >
            <Globe size={15} /> Landing page própria
          </button>
        </div>

        {destinationType === "whatsapp" ? (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1.5fr", gap: 14 }}>
            <div>
              <label style={labelStyle}>Número de WhatsApp <span style={{ color: "#ef4444" }}>*</span></label>
              <input value={whatsappNumber} onChange={e => setWhatsappNumber(e.target.value)} placeholder="5511999999999" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Mensagem pré-preenchida <span style={{ color: "hsl(var(--muted-foreground))", fontWeight: 400 }}>(opcional)</span></label>
              <input value={whatsappMessage} onChange={e => setWhatsappMessage(e.target.value)} placeholder="Olá! Vim pelo anúncio..." style={inputStyle} />
            </div>
          </div>
        ) : (
          <div>
            <label style={labelStyle}>URL da landing page <span style={{ color: "#ef4444" }}>*</span></label>
            <input value={destinationUrl} onChange={e => setDestinationUrl(e.target.value)} placeholder="https://meusite.com.br/promo" style={inputStyle} />
          </div>
        )}
      </div>

      <Button onClick={handleSubmit} disabled={saving} className="w-full bg-blue-600 hover:bg-blue-700 text-white">
        {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
        {saving ? "Criando..." : "Criar link"}
      </Button>
    </div>
  );
}

function LinkCard({ link, onDelete }) {
  const platformInfo = PLATFORMS.find(p => p.value === link.platform) || PLATFORMS[4];
  return (
    <div style={{ background: "hsl(var(--secondary))", border: "1px solid hsl(var(--border))", borderRadius: 14, padding: 16, marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", marginBottom: 2, display: "flex", alignItems: "center", gap: 5 }}>
            <PlatformIcon platform={link.platform} size={30} /> {platformInfo.label}
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "hsl(var(--foreground))", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {link.label}
          </div>
        </div>
        <button
          onClick={() => onDelete(link.id)}
          style={{ background: "none", border: "none", cursor: "pointer", color: "hsl(var(--muted-foreground))", padding: 4, flexShrink: 0 }}
          title="Excluir link"
        >
          <Trash2 size={16} />
        </button>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <code style={{
          fontSize: 12.5, background: "hsl(var(--border))", padding: "6px 10px", borderRadius: 8,
          color: "hsl(var(--foreground))", flex: 1, minWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {link.short_url}
        </code>
        <CopyButton text={link.short_url} />
        <a href={link.destination} target="_blank" rel="noopener noreferrer" style={{ color: "hsl(var(--muted-foreground))", display: "flex" }} title="Ver destino final">
          <ExternalLink size={15} />
        </a>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
        <div style={{ background: "hsl(var(--border))", borderRadius: 10, padding: "8px 10px", textAlign: "center" }}>
          <MousePointerClick size={13} color="#3b82f6" style={{ marginBottom: 3 }} />
          <div style={{ fontSize: 15, fontWeight: 800, color: "hsl(var(--foreground))" }}>{link.clicks}</div>
          <div style={{ fontSize: 10, color: "hsl(var(--muted-foreground))" }}>cliques</div>
        </div>
        <div style={{ background: "hsl(var(--border))", borderRadius: 10, padding: "8px 10px", textAlign: "center" }}>
          <ShoppingBag size={13} color="#f59e0b" style={{ marginBottom: 3 }} />
          <div style={{ fontSize: 15, fontWeight: 800, color: "hsl(var(--foreground))" }}>{link.sales_count}</div>
          <div style={{ fontSize: 10, color: "hsl(var(--muted-foreground))" }}>vendas</div>
        </div>
        <div style={{ background: "hsl(var(--border))", borderRadius: 10, padding: "8px 10px", textAlign: "center" }}>
          <DollarSign size={13} color="#22c55e" style={{ marginBottom: 3 }} />
          <div style={{ fontSize: 13, fontWeight: 800, color: "hsl(var(--foreground))" }}>{fmtBRL(link.revenue)}</div>
          <div style={{ fontSize: 10, color: "hsl(var(--muted-foreground))" }}>receita</div>
        </div>
      </div>
    </div>
  );
}

function HowItWorksGuide() {
  const [open, setOpen] = useState(true);

  const step = {
    display: "flex", gap: 12, alignItems: "flex-start",
    padding: "12px 0", borderBottom: "1px solid hsl(var(--border))",
  };
  const stepNum = (color) => ({
    width: 26, height: 26, borderRadius: "50%", flexShrink: 0,
    background: color, color: "#fff", fontSize: 12.5, fontWeight: 800,
    display: "flex", alignItems: "center", justifyContent: "center",
  });

  return (
    <div style={{ background: "hsl(var(--secondary))", border: "1px solid hsl(var(--border))", borderRadius: 16, marginBottom: 24, overflow: "hidden" }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: 18, background: "none", border: "none", cursor: "pointer",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 15, fontWeight: 700, color: "hsl(var(--foreground))" }}>
          💡 Como funciona? <span style={{ fontSize: 12, fontWeight: 500, color: "hsl(var(--muted-foreground))" }}>(veja um exemplo completo)</span>
        </span>
        {open ? <ChevronUp size={18} color="hsl(var(--muted-foreground))" /> : <ChevronDown size={18} color="hsl(var(--muted-foreground))" />}
      </button>

      {open && (
        <div style={{ padding: "0 18px 20px" }}>
          <p style={{ fontSize: 13, color: "hsl(var(--muted-foreground))", marginBottom: 16, lineHeight: 1.6 }}>
            Aqui está um exemplo real: você anuncia um curso no <strong>Meta Ads</strong> e vende esse curso na <strong>Hotmart</strong>.
            Veja o caminho completo de um clique até a venda aparecer aqui no Dashboard.
          </p>

          <div style={step}>
            <div style={stepNum("#3b82f6")}>1</div>
            <div>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: "hsl(var(--foreground))", marginBottom: 3 }}>
                Crie o link aqui na página UTMs
              </div>
              <div style={{ fontSize: 12.5, color: "hsl(var(--muted-foreground))", lineHeight: 1.6 }}>
                Plataforma: <code style={{ background: "hsl(var(--border))", padding: "1px 6px", borderRadius: 5 }}>Meta Ads</code>,
                utm_campaign: <code style={{ background: "hsl(var(--border))", padding: "1px 6px", borderRadius: 5 }}>curso_julho</code>,
                destino: <strong>Landing page</strong> → cole a URL de checkout do seu curso na Hotmart.
                Isso gera algo como <code style={{ background: "hsl(var(--border))", padding: "1px 6px", borderRadius: 5 }}>autopostbr.com.br/r/abc1234</code>.
              </div>
            </div>
          </div>

          <div style={step}>
            <div style={stepNum("#6366f1")}>2</div>
            <div>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: "hsl(var(--foreground))", marginBottom: 3 }}>
                Use esse link curto no anúncio do Meta Ads
              </div>
              <div style={{ fontSize: 12.5, color: "hsl(var(--muted-foreground))", lineHeight: 1.6 }}>
                No Gerenciador de Anúncios da Meta, coloque <code style={{ background: "hsl(var(--border))", padding: "1px 6px", borderRadius: 5 }}>autopostbr.com.br/r/abc1234</code> como
                a URL de destino do seu anúncio (em vez do link direto da Hotmart).
              </div>
            </div>
          </div>

          <div style={step}>
            <div style={stepNum("#f59e0b")}>3</div>
            <div>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: "hsl(var(--foreground))", marginBottom: 3 }}>
                Configure o parâmetro de rastreio no checkout da Hotmart
              </div>
              <div style={{ fontSize: 12.5, color: "hsl(var(--muted-foreground))", lineHeight: 1.6 }}>
                <strong>Esse passo é manual e é o que liga tudo:</strong> no painel da Hotmart, no link de divulgação do seu
                produto, adicione <code style={{ background: "hsl(var(--border))", padding: "1px 6px", borderRadius: 5 }}>?src=abc1234</code> (use
                o mesmo código do seu link curto). Sem isso, a venda até é registrada, mas aparece como "sem atribuição" —
                o sistema não tem como adivinhar de qual campanha ela veio.
              </div>
            </div>
          </div>

          <div style={step}>
            <div style={stepNum("#ec4899")}>4</div>
            <div>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: "hsl(var(--foreground))", marginBottom: 3 }}>
                Configure o webhook da Hotmart (uma vez só)
              </div>
              <div style={{ fontSize: 12.5, color: "hsl(var(--muted-foreground))", lineHeight: 1.6 }}>
                Cole a URL e o Hottok na seção "Integração com Hotmart" logo abaixo. Assim, toda venda aprovada chega
                automaticamente aqui.
              </div>
            </div>
          </div>

          <div style={{ ...step, borderBottom: "none", paddingBottom: 0 }}>
            <div style={stepNum("#22c55e")}>5</div>
            <div>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: "hsl(var(--foreground))", marginBottom: 3 }}>
                Pronto! A venda aparece no Dashboard do Meta Ads
              </div>
              <div style={{ fontSize: 12.5, color: "hsl(var(--muted-foreground))", lineHeight: 1.6 }}>
                Alguém clica no anúncio → cai no checkout da Hotmart → compra → você vê, na seção "Links UTM" do
                Dashboard, que essa campanha específica gerou aquela venda — sem precisar registrar nada manualmente.
              </div>
            </div>
          </div>

          <div style={{
            marginTop: 16, padding: "12px 14px", borderRadius: 10,
            background: "rgba(245,158,11,0.07)", border: "1px solid rgba(245,158,11,0.25)",
          }}>
            <p style={{ fontSize: 12, color: "hsl(var(--foreground))", margin: 0, lineHeight: 1.6 }}>
              ⚠️ <strong>O passo 3 é o único manual.</strong> Se você esquecer de colocar o <code style={{ background: "hsl(var(--border))", padding: "1px 5px", borderRadius: 4 }}>src</code> no
              link de checkout da Hotmart, a venda ainda é registrada automaticamente — só não fica vinculada a uma
              campanha específica.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function HotmartConfigSection() {
  const [open, setOpen] = useState(false);
  const [hottok, setHottok] = useState("");
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchConfig = async () => {
    try {
      const { data } = await api.get("/utm/hotmart-config");
      setConfig(data);
    } catch {}
  };

  useEffect(() => { fetchConfig(); }, []);

  const handleSave = async () => {
    if (!hottok.trim()) { toast.error("Cole o Hottok copiado do painel da Hotmart."); return; }
    setSaving(true);
    try {
      await api.post("/utm/hotmart-config", { hottok: hottok.trim() });
      toast.success("Webhook da Hotmart configurado! 🎉");
      setHottok("");
      fetchConfig();
    } catch (e) {
      toast.error(getErrorMessage(e, "Erro ao salvar"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ background: "hsl(var(--secondary))", border: "1px solid hsl(var(--border))", borderRadius: 16, marginBottom: 24, overflow: "hidden" }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: 18, background: "none", border: "none", cursor: "pointer",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 15, fontWeight: 700, color: "hsl(var(--foreground))" }}>
          <img src="/icons/hotmart.svg" alt="Hotmart" style={{ height: 40, width: "auto" }} />
          Integração com Hotmart (vendas automáticas)
          {config?.configured && (
            <span style={{ fontSize: 11, fontWeight: 700, color: "#22c55e", background: "rgba(34,197,94,0.1)", padding: "2px 9px", borderRadius: 20 }}>
              Configurado ✓
            </span>
          )}
        </span>
        {open ? <ChevronUp size={18} color="hsl(var(--muted-foreground))" /> : <ChevronDown size={18} color="hsl(var(--muted-foreground))" />}
      </button>

      {open && (
        <div style={{ padding: "0 18px 18px" }}>
          <p style={{ fontSize: 13, color: "hsl(var(--muted-foreground))", marginBottom: 14, lineHeight: 1.6 }}>
            Configure o webhook abaixo no painel da Hotmart (como Produtor) para registrar vendas automaticamente
            aqui, atribuídas ao link rastreável usado na divulgação (campo <code>src</code> ou <code>sck</code> do link).
          </p>

          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12.5, fontWeight: 600, color: "hsl(var(--muted-foreground))", marginBottom: 6, display: "block" }}>
              1. URL do webhook <span style={{ fontWeight: 400 }}>(cole no painel da Hotmart → Webhook → URL de envio de dados)</span>
            </label>
            <div style={{ display: "flex", gap: 8 }}>
              <code style={{
                fontSize: 12.5, background: "hsl(var(--border))", padding: "9px 12px", borderRadius: 10,
                color: "hsl(var(--foreground))", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>
                {config?.webhook_url || "carregando..."}
              </code>
              {config?.webhook_url && <CopyButton text={config.webhook_url} />}
            </div>
          </div>

          <div>
            <label style={{ fontSize: 12.5, fontWeight: 600, color: "hsl(var(--muted-foreground))", marginBottom: 6, display: "block" }}>
              2. Hottok <span style={{ fontWeight: 400 }}>(copie em Hotmart → Ferramentas → Webhook → Autenticação)</span>
            </label>
            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ position: "relative", flex: 1 }}>
                <KeyRound size={14} color="hsl(var(--muted-foreground))" style={{ position: "absolute", left: 12, top: 12 }} />
                <input
                  value={hottok}
                  onChange={e => setHottok(e.target.value)}
                  placeholder="Cole o Hottok aqui"
                  style={{
                    width: "100%", padding: "9px 12px 9px 34px", borderRadius: 10,
                    border: "1px solid hsl(var(--border))", background: "hsl(var(--secondary))", color: "hsl(var(--foreground))", fontSize: 13.5, outline: "none",
                  }}
                />
              </div>
              <Button onClick={handleSave} disabled={saving} className="bg-orange-600 hover:bg-orange-700 text-white">
                {saving ? <Loader2 size={15} className="animate-spin" /> : "Salvar"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SectionLabel({ icon, text, last }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      marginTop: 8, marginBottom: 14,
    }}>
      <span style={{ fontSize: 15 }}>{icon}</span>
      <span style={{
        fontSize: 11.5, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase",
        color: "hsl(var(--muted-foreground))", whiteSpace: "nowrap",
      }}>
        {text}
      </span>
      <div style={{ flex: 1, height: 1, background: "hsl(var(--border))" }} />
    </div>
  );
}

export default function UtmPage() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLinks = async () => {
    try {
      const { data } = await api.get("/utm/links");
      setLinks(data);
    } catch (e) {
      toast.error(getErrorMessage(e, "Erro ao carregar links"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLinks(); }, []);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/utm/links/${id}`);
      toast.success("Link removido");
      setLinks(ls => ls.filter(l => l.id !== id));
    } catch (e) {
      toast.error(getErrorMessage(e, "Erro ao remover"));
    }
  };

  const totalClicks = links.reduce((s, l) => s + (l.clicks || 0), 0);
  const totalRevenue = links.reduce((s, l) => s + (l.revenue || 0), 0);

  return (
    <div style={{ maxWidth: 760, margin: "0 auto", padding: "28px 20px 60px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
        <Link2 size={22} color="#3b82f6" />
        <h1 style={{ fontSize: 22, fontWeight: 800, color: "hsl(var(--foreground))" }}>UTMs</h1>
      </div>
      <p style={{ fontSize: 13.5, color: "hsl(var(--muted-foreground))", marginBottom: 24 }}>
        Crie links rastreáveis para suas campanhas (Meta Ads, Google Ads, TikTok Ads, Hotmart e mais) e acompanhe
        cliques e vendas atribuídas, direto no Dashboard do Meta Ads.
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 36 }}>
        <div style={{ background: "hsl(var(--secondary))", border: "1px solid hsl(var(--border))", borderRadius: 14, padding: "14px 16px" }}>
          <div style={{ fontSize: 11.5, color: "hsl(var(--muted-foreground))", marginBottom: 4 }}>Total de cliques</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: "hsl(var(--foreground))" }}>{totalClicks}</div>
        </div>
        <div style={{ background: "hsl(var(--secondary))", border: "1px solid hsl(var(--border))", borderRadius: 14, padding: "14px 16px" }}>
          <div style={{ fontSize: 11.5, color: "hsl(var(--muted-foreground))", marginBottom: 4 }}>Receita atribuída</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: "hsl(var(--foreground))" }}>{fmtBRL(totalRevenue)}</div>
        </div>
      </div>

      <SectionLabel icon="💡" text="Como funciona" />
      <HowItWorksGuide />

      <SectionLabel icon="🟠" text="Integração com plataformas de venda" />
      <HotmartConfigSection />

      <SectionLabel icon="➕" text="Criar link" />
      <CreateLinkForm onCreated={fetchLinks} />

      <SectionLabel icon="🔗" text={`Seus links (${links.length})`} last />

      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Loader2 size={24} className="animate-spin" style={{ margin: "0 auto" }} color="hsl(var(--muted-foreground))" />
        </div>
      ) : links.length === 0 ? (
        <div style={{
          textAlign: "center", padding: "40px 20px", color: "hsl(var(--muted-foreground))",
          background: "hsl(var(--secondary))", border: "1px dashed hsl(var(--border))", borderRadius: 14,
        }}>
          <Link2 size={32} style={{ opacity: 0.2, display: "block", margin: "0 auto 10px" }} />
          <p style={{ fontWeight: 600, color: "hsl(var(--foreground))" }}>Nenhum link criado ainda</p>
          <p style={{ fontSize: 12.5 }}>Crie o primeiro acima para começar a rastrear suas campanhas</p>
        </div>
      ) : (
        links.map(link => <LinkCard key={link.id} link={link} onDelete={handleDelete} />)
      )}
    </div>
  );
}
